/***************************************************************************/
"use client";
/***************************************************************************/

import { useEffectOnUserThemeModeChange } from "@/client/events/user/theme-mode";
import { cn, hslVariable2hex } from "@/client/utils/css";
import React, { useEffect, useState } from "react";
import { TCssColorVariable } from "../utils/css/types";

const AirFiLogo: React.FC<
  React.HTMLAttributes<SVGElement> & { color?: TCssColorVariable }
> = ({ className, color, ...props }) => {
  const [logoColor, setLogoColor] = useState<string | undefined>(undefined);

  useEffect(() => {
    const t = setTimeout(() => {
      setLogoColor(hslVariable2hex(color ? color : "--foreground"));
    }, 0);

    return () => clearTimeout(t);
  }, []);

  useEffectOnUserThemeModeChange(() => {
    setLogoColor(hslVariable2hex(color ? color : "--foreground"));
  }, []);

  return (
    <svg
      version="1.0"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 340.000000 80.000000"
      preserveAspectRatio="xMidYMid meet"
      {...props}
      className={cn("", className)}
    >
      <g
        transform="translate(0.000000,80.000000) scale(0.100000,-0.100000)"
        fill={logoColor}
        stroke="none"
      >
        <path
          d="M495 786 c-12 -8 -16 -19 -11 -31 5 -15 15 -17 79 -11 184 18 348
-64 459 -226 64 -95 115 -70 54 27 -73 114 -189 202 -315 239 -70 20 -239 22
-266 2z"
        />
        <path
          d="M560 691 c-29 -5 -35 -10 -35 -31 0 -24 2 -25 55 -22 147 6 273 -56
347 -174 38 -59 84 -59 69 2 -10 39 -129 157 -188 186 -80 40 -168 53 -248 39z"
        />
        <path
          d="M233 620 c-85 -17 -159 -74 -201 -154 -36 -69 -38 -189 -5 -252 32
-61 69 -101 122 -132 46 -27 46 -27 274 -30 218 -3 230 -2 274 19 53 26 99 90
108 154 19 122 -81 234 -207 235 -42 0 -49 3 -62 29 -48 92 -190 153 -303 131z
m164 -79 c45 -23 89 -70 109 -117 13 -32 13 -32 76 -27 54 4 68 2 98 -19 97
-66 89 -208 -15 -258 -36 -18 -62 -20 -217 -20 -245 0 -309 23 -364 132 -22
44 -25 61 -22 119 3 56 9 76 33 110 36 50 67 75 121 95 50 18 129 12 181 -15z"
        />
        <path
          d="M570 565 c-24 -29 -4 -45 54 -45 79 0 129 -23 184 -87 35 -39 52 -52
65 -47 58 22 -57 155 -158 183 -58 16 -130 14 -145 -4z"
        />
        <path
          d="M3190 369 c-70 -28 -110 -106 -90 -178 21 -78 92 -121 178 -107 98
16 151 126 103 216 -33 64 -123 96 -191 69z m115 -74 c53 -52 19 -155 -50
-155 -47 0 -64 8 -80 41 -22 41 -18 73 11 108 33 40 83 43 119 6z"
        />
        <path
          d="M995 238 c-31 -73 -59 -139 -62 -145 -4 -9 6 -13 30 -13 29 0 37 5
47 30 12 29 15 30 75 30 60 0 63 -1 75 -30 10 -25 18 -30 47 -30 41 0 44 -17
-34 163 -55 125 -56 127 -88 127 -32 0 -33 -1 -90 -132z m125 -30 c0 -5 -16
-8 -35 -8 -19 0 -35 2 -35 5 0 2 8 20 17 40 l17 36 18 -33 c9 -18 17 -36 18
-40z"
        />
        <path d="M1280 225 l0 -145 30 0 30 0 0 145 0 145 -30 0 -30 0 0 -145z" />
        <path
          d="M1410 225 l0 -145 35 0 c35 0 35 0 35 45 0 40 2 45 23 45 16 0 33
-14 55 -45 27 -39 36 -45 67 -45 19 0 35 3 35 6 0 3 -14 25 -31 50 l-30 45 25
30 c41 48 30 125 -21 148 -13 6 -62 11 -108 11 l-85 0 0 -145z m151 85 c21
-12 25 -50 7 -68 -7 -7 -29 -12 -50 -12 l-38 0 0 45 c0 44 1 45 31 45 17 0 39
-5 50 -10z"
        />
        <path
          d="M1720 225 l0 -145 30 0 c28 0 29 1 32 58 l3 57 63 3 c58 3 62 4 62
27 0 24 -3 25 -65 25 l-65 0 0 35 0 35 70 0 c68 0 70 1 70 25 l0 25 -100 0
-100 0 0 -145z"
        />
        <path d="M1970 225 l0 -145 35 0 35 0 0 145 0 145 -35 0 -35 0 0 -145z" />
        <path
          d="M2265 258 c-26 -62 -54 -128 -62 -145 l-13 -33 35 0 c31 0 38 4 46
30 11 29 13 30 75 30 63 0 65 -1 75 -30 9 -26 16 -30 46 -30 41 0 43 -8 -32
163 -52 119 -54 122 -87 125 l-35 3 -48 -113z m100 -14 c19 -47 20 -44 -19
-44 l-34 0 15 40 c9 22 18 40 19 40 2 0 11 -16 19 -36z"
        />
        <path
          d="M2540 225 l0 -145 105 0 105 0 0 30 0 30 -75 0 -75 0 0 30 0 30 65 0
c62 0 65 1 65 25 0 23 -4 24 -62 27 -60 3 -63 4 -66 30 -3 27 -3 27 70 30 71
3 73 4 76 31 l3 27 -105 0 -106 0 0 -145z"
        />
        <path
          d="M2810 224 l0 -145 33 3 c30 3 32 6 35 46 4 60 36 59 77 -3 27 -39 34
-45 68 -45 20 0 37 2 37 5 0 3 -15 26 -34 52 l-33 46 26 23 c21 18 26 31 26
69 0 39 -5 51 -28 71 -25 21 -37 24 -118 24 l-89 0 0 -146z m151 86 c21 -12
25 -50 7 -68 -7 -7 -29 -12 -50 -12 l-38 0 0 45 c0 44 1 45 31 45 17 0 39 -5
50 -10z"
        />
        <path
          d="M2101 131 c-13 -24 8 -55 34 -50 28 6 42 34 26 53 -18 22 -48 20 -60
-3z"
        />
      </g>
    </svg>
  );
};

export { AirFiLogo };
